import { useMemo, useState } from 'react';
import { exerciseNavTarget } from '@/lib/exercise-route';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarRange, ChevronRight, ClipboardCheck, Layers, Zap } from 'lucide-react';
import { Card, CardContent, cn } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useStudentProfile } from '@/hooks/use-student-profile';
import { useInProgressExercises } from '@/hooks/use-in-progress-exercises';
import { useLaunchPending } from '@/hooks/use-launch-pending';
import { Spinner } from '@/components/spinner';
import { DailyExerciseSection } from '@/components/student-objectives/daily-exercise-section';
import { StudentTodaySection } from '@/components/student-today-section';
import { useStudentHomework } from '@/lib/student/homework';
import { buildTodayItems, type TodayItem } from '@/lib/student/today';
import type { SchoolLevel } from '@jeraisonne/db';

// ─── Types ────────────────────────────────────────────────────────────────────

type Chapter = { id: string; title: string; subject: string; level: string; slug: string };

// ─── Page ─────────────────────────────────────────────────────────────────────

/** Autres entraînements (pages existantes) — accessibles aussi depuis l'Accueil. */
const MORE_TRAINING = [
  {
    to: '/student/automatismes',
    title: 'Automatismes',
    subtitle: '10 questions flash',
    icon: Zap,
    tone: 'bg-accent-500 text-white',
  },
  {
    to: '/student/progression',
    title: 'Mon année',
    subtitle: 'Le programme, pack par pack',
    icon: CalendarRange,
    tone: 'bg-primary-600 text-white',
  },
  {
    to: '/student/diagnostic',
    title: 'Diagnostic',
    subtitle: 'Où j’en suis',
    icon: ClipboardCheck,
    tone: 'bg-[#10b981] text-white',
  },
  {
    to: '/student/skillcards',
    title: 'Mes SKILLCARDS',
    subtitle: 'Mes cartes à travailler',
    icon: Layers,
    tone: 'bg-[#8b5cf6] text-white',
  },
] as const;

export function StudentRevisePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const studentId = user?.id ?? '';

  const { data: studentProfile } = useStudentProfile(user?.id);
  const level = studentProfile?.level as SchoolLevel | null | undefined;

  const { data: inProgress = [], isLoading: loadingInProgress } = useInProgressExercises(studentId);
  const homework = useStudentHomework(studentId);
  const { pendingId, launch } = useLaunchPending();

  // Devoirs (prof + classe) puis exercices en cours — sans le diagnostic.
  const items = useMemo(
    () =>
      buildTodayItems({
        homework: homework.data ?? [],
        inProgress,
        diagnostic: null,
        maxResume: 10,
      }),
    [homework.data, inProgress],
  );

  // ── Navigation helpers ──────────────────────────────────────────────────────

  function goToExercise(chapter: Pick<Chapter, 'subject' | 'level' | 'slug'>, exerciseId: string) {
    navigate({
      to: '/learn/$subject/$level/$chapter/$exercise',
      params: {
        subject: chapter.subject,
        level: chapter.level,
        chapter: chapter.slug,
        exercise: exerciseId,
      },
    });
  }

  async function handleGoToExerciseFromId(exerciseId: string): Promise<boolean> {
    const { data: ex } = await supabase
      .from('exercises')
      .select('chapter_id, chapters ( id, title, subject, level, slug )')
      .eq('id', exerciseId)
      .single();
    if (!ex) return false;
    const ch = ex.chapters as unknown as Chapter | null;
    // Exercice sans chapitre (SKILLCARD) : lecteur direct.
    navigate(exerciseNavTarget(exerciseId, ch));
    return true;
  }

  // Spinner sur le bouton cliqué jusqu'à l'ouverture de l'exercice.
  function startExercise(exerciseId: string) {
    void launch(exerciseId, () => handleGoToExerciseFromId(exerciseId)).catch(console.error);
  }

  function launchItem(item: Extract<TodayItem, { kind: 'homework' | 'resume' }>) {
    void launch(item.key, () => {
      const exerciseId =
        item.kind === 'homework' ? item.homework.exerciseId : item.exercise.exerciseId;
      const chapter =
        item.kind === 'homework'
          ? item.homework.chapter
          : item.exercise.chapterSlug
            ? {
                subject: item.exercise.subject,
                level: item.exercise.level,
                slug: item.exercise.chapterSlug,
              }
            : null;
      if (!chapter) return handleGoToExerciseFromId(exerciseId);
      goToExercise(chapter, exerciseId);
      return true;
    }).catch(console.error);
  }

  return (
    <div className="mx-auto max-w-lg space-y-7 px-4 py-6 pb-24 sm:py-8">
      <header>
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">S’entraîner</h1>
        <p className="text-ink-500 mt-1 text-sm">
          Reprends tes exercices, fais tes devoirs ou échauffe-toi.
        </p>
      </header>

      {/* Devoirs du prof (individuel + classe) et exercices en cours — masqué si rien */}
      <StudentTodaySection
        title="Mes exercices"
        items={items}
        isLoading={loadingInProgress || homework.isLoading}
        pendingKey={pendingId}
        onLaunch={launchItem}
        hideWhenEmpty
      />

      {/* Exercice du jour */}
      {level ? (
        <DailyExerciseSection
          studentLevel={level}
          onStart={startExercise}
          pendingExerciseId={pendingId}
        />
      ) : (
        <section className="space-y-3">
          <h2 className="font-display text-ink-900 flex items-center gap-2 text-lg font-bold">
            <span aria-hidden>☀️</span>
            <span>Exercice du jour</span>
          </h2>
          <Card className="border-ink-100">
            <CardContent className="py-4">
              <p className="text-ink-400 text-sm">
                Complète ton profil pour accéder à l'exercice du jour.
              </p>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Warmup gratuit */}
      <WarmupCard studentId={studentId} />

      {/* Autres entraînements */}
      <section className="space-y-3" aria-labelledby="more-training-title">
        <h2 id="more-training-title" className="font-display text-ink-900 text-lg font-bold">
          Autres entraînements
        </h2>
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {MORE_TRAINING.map(({ to, title, subtitle, icon: Icon, tone }) => (
            <Link
              key={to}
              to={to}
              className="border-ink-100 flex min-h-[2.75rem] flex-col gap-2 rounded-2xl border bg-white p-3 active:scale-[0.98]"
            >
              <span
                className={cn('flex h-10 w-10 items-center justify-center rounded-xl', tone)}
                aria-hidden
              >
                <Icon className="h-5 w-5" />
              </span>
              <span>
                <span className="font-display text-ink-900 block text-sm font-bold">{title}</span>
                <span className="text-ink-500 block text-xs">{subtitle}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Warmup card ──────────────────────────────────────────────────────────────

function WarmupCard({ studentId }: { studentId: string }) {
  // Spinner sur le bouton jusqu'à l'ouverture du warmup.
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();

  const { data: lastWarmup } = useQuery({
    queryKey: ['last-warmup', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('attempts')
        .select('score, completed_at')
        .eq('student_id', studentId)
        .eq('session_type', 'warmup')
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data ?? null;
    },
  });

  const { data: warmupIllustration } = useQuery({
    queryKey: ['warmup-template-illustration'],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from('objective_templates')
        .select('illustration_url')
        .eq('type', 'warmup')
        .order('display_order', { ascending: true })
        .limit(1)
        .maybeSingle();
      return (data as { illustration_url: string | null } | null)?.illustration_url ?? null;
    },
  });

  return (
    <section className="space-y-3">
      <button
        type="button"
        aria-busy={starting || undefined}
        disabled={starting}
        onClick={() => {
          setStarting(true);
          void navigate({ to: '/student/warmup' });
        }}
        className="w-full text-left active:scale-[0.99]"
      >
        <Card className="border-ink-100 relative min-h-[13rem] overflow-hidden bg-white">
          {warmupIllustration && (
            <>
              <img
                src={warmupIllustration}
                alt=""
                loading="lazy"
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 h-full w-3/5 object-cover"
              />
              <div
                aria-hidden="true"
                className="bg-linear-to-r ocean:from-[#0b1d47] ocean:via-[#0b1d47]/75 pointer-events-none absolute inset-0 from-white from-40% via-white/70 via-55% to-transparent"
              />
            </>
          )}
          <CardContent className="relative flex min-h-[13rem] flex-col p-5">
            <div className="flex items-center gap-2">
              <span className="bg-accent-100 flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
                <Zap className="text-accent-500 h-6 w-6" fill="currentColor" />
              </span>
              <span className="rounded-full bg-[#10b981] px-2.5 py-1 text-xs font-bold uppercase text-white">
                Gratuit
              </span>
            </div>
            <p className="font-display text-ink-900 mt-3 text-2xl font-extrabold leading-tight">
              Warmup gratuit
            </p>
            <p className="text-ink-500 mt-1 max-w-[60%] text-sm leading-snug">
              10 questions pour échauffer les neurones
            </p>
            <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-4">
              {lastWarmup?.score != null ? (
                <p className="text-ink-500 text-sm">
                  Dernier score :{' '}
                  <span className="font-display text-primary-600 text-xl font-extrabold">
                    {lastWarmup.score}%
                  </span>
                </p>
              ) : (
                <span />
              )}
              <div className="flex h-11 shrink-0 items-center gap-1 rounded-full bg-[linear-gradient(180deg,#f7d068,#f4c64e)] px-5 text-sm font-bold text-[#0a2e57] shadow-[0_4px_0_#c79a26]">
                {starting ? (
                  <>
                    <Spinner />
                    Préparation…
                  </>
                ) : (
                  <>
                    {lastWarmup ? 'Recommencer' : 'Commencer'}
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </button>
    </section>
  );
}
